import { AxiosResponse } from "axios";
import { peopleMentionBaseConfig } from "components/Quill/Mention/people/mentionConfig";
import { AutoCompleteDataModelDtoPaginatedList, DiscussionListFilter, DiscussionType, UserSelectionEntityType } from "generated/CoreApi_v1";
import moment from "moment";
import { useCoreApi } from "providers/CoreApiProvider";
import { useRef } from "react";
import { NIL } from "uuid";
import { DiscussionBase, DiscussionMode } from "views/foundation/discussion/components/DiscussionBase";

export interface IClassroomDiscussionProps {
    classroomId: string;
    enrollmentId: string | null;
    title?: string;
    readOnly?: boolean;
    initialValue?: string;
    enableInstructorPrivacyOption: boolean;
    disableEditPrivacy: boolean;
    discussionMode?: DiscussionMode;
    initialListFilter?: DiscussionListFilter;
    dialogMode?: boolean;

    onUpdateCommentCounter?: (totalComments: number) => void;
}

/**
 * Discussões de uma turma.
 * @param props
 */
export const ClassroomDiscussionBox: React.FC<IClassroomDiscussionProps> = (props) => {

    const { classroomId, enrollmentId, title, readOnly, enableInstructorPrivacyOption, disableEditPrivacy, discussionMode, onUpdateCommentCounter } = props;
    const api = useCoreApi();
    const discussionType = useRef<DiscussionType>(DiscussionType.Public);

    const lastView = useRef<Date>(moment().toDate());

    function getMembers(searchText: string): Promise<AxiosResponse<AutoCompleteDataModelDtoPaginatedList>> {
        const inputValue = searchText.trim().toLowerCase();
        return api.v1.lmsSuggestions.userDiscussionSuggestions({
            classroomId,
            discussionType: discussionType.current,
            pageNumber: 1,
            pageSize: 10,
            searchText: inputValue,
            selectionHistoryType: UserSelectionEntityType.UserMention,
            ignoreSelectionHistory: false
        });
    }

    const configPeopleMention = {
        mention: {
            ...peopleMentionBaseConfig,
            source: function (searchTerm: any, renderList: any, mentionChar: any) {
                if (mentionChar === "@") {
                    getMembers(searchTerm)
                        .then((users) => {
                            var values = users.data.items.map((d) => ({ id: d.key, ot: "user", value: d.label }));
                            renderList(values, searchTerm);
                        })
                        .catch(() => {
                            renderList([], searchTerm);
                        });
                }
            }
        }
    };

    return (
        <DiscussionBase
            referenceId={enrollmentId ?? NIL}
            title={title ?? ""}
            counterMode="thread"
            readOnly={readOnly}
            enableInstructorPrivacyOption={enableInstructorPrivacyOption}
            disableEditPrivacy={disableEditPrivacy}
            configPeopleMention={configPeopleMention}
            initialValue={props.initialValue}
            initialListFilter={props.initialListFilter}
            dialogMode={props.dialogMode}
            discussionMode={discussionMode}
            onUpdateCommentCounter={onUpdateCommentCounter}
            onGetDiscussions={(parentId, startSequenceNum, take, filter, orderBy, discussionId) =>
                api.v1.lmsDiscussion.getDiscussionClassroom({
                    parentId,
                    startSequenceNum,
                    take,
                    filter,
                    orderBy,
                    discussionId,
                    enrollmentId: enrollmentId == null || enrollmentId == NIL ? null : enrollmentId,
                    classroomId: enrollmentId == null || enrollmentId == NIL ? classroomId : null,
                    lastView: lastView.current
                })
            }
            onPostNew={({ parentId, replyId, message, discussionType }) =>
                api.v1.lmsDiscussion.createDiscussionClassroom({
                    classroomId,
                    parentId,
                    replyId,
                    message,
                    discussionType
                })
            }
            onPostUpdate={(discussionId, message, discussionType) =>
                api.v1.lmsDiscussion.updateDiscussionClassroom({ classroomId, discussionId, message, discussionType })
            }
            onPostDelete={(discussionId) =>
                api.v1.lmsDiscussion.deleteDiscussionClassroom({
                    classroomId,
                    discussionId
                })
            }
            onPostLike={(discussionId, like) =>
                api.v1.lmsDiscussion.likeDiscussionClassroom({
                    classroomId,
                    discussionId,
                    like
                })
            }
            onChangeDiscussionType={(value) => discussionType.current = value}
            onGetPeopleData={(p, signal) => api.v1.lmsSelectors.userDiscussionSelector({
                classroomId: classroomId,
                discussionType: discussionType.current,
                searchText: p.searchText,
                pageNumber: p.pageNumber,
                pageSize: p.pageSize,
                selectionHistoryType: UserSelectionEntityType.UserMention,
                ignoreSelectionHistory: p.ignoreSelectionHistory
            }, { signal })}
        />
    );
};
